import * as React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { graphql } from 'react-apollo';

import PropTypes from 'prop-types';
import { compose, getContext, setDisplayName } from 'recompose';

import type { EventData, WithTrackingData } from 'js/lib/retracked';
import Retracked from 'js/lib/retracked';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import useDebounce from 'bundles/common/hooks/useDebounce';
import type { PropsFromCaller } from 'bundles/enterprise-legacy-learner-home/components/single-program/SkillSetsTabContent';
import GetAllOccupationsQuery from 'bundles/enterprise-legacy-learner-home/queries/GetAllOccupations.graphql';
import type {
  GetAllOccupationsQuery as GetAllOccupationsQueryData,
  GetAllOccupationsQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/GetAllOccupations';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import type { SkillSetListQuery_EnterpriseTargetSkillProfileSummariesV1Resource_byProgram_elements as TargetSkillProficiencySummary } from 'bundles/program-home/components/enterprise-home/__generated__/SkillSetListQuery';
import type { org_coursera_skill_GoalType as GoalType } from 'bundles/program-home/components/enterprise-home/__generated__/globalTypes';

type PropsFromSkillSetListQuery = {
  skillsets: TargetSkillProficiencySummary[];
  loading: boolean;
  error: boolean;
};

export type AllOccupations = Record<string, string | undefined>;

type PropsFromOccupationMetadataQuery = {
  allOccupations: AllOccupations;
};

export type SkillNames = Record<string, string | undefined>;

export type FilterOptions = {
  roles: Set<string>;
  skills: Set<string>;
  goals: Set<GoalType>;
};

export type FilterSelections = {
  role: string;
  skills: string;
  goal: string;
};

export type FilterChangeHandler = (newFilterSelections: FilterSelections) => void;

export type SelectOption = {
  value: string;
  label: string;
};

type PropsFromGetContext = {
  _eventData: EventData;
  _withTrackingData: WithTrackingData;
};

type FilterInputChangeHandler = (e: string, type: 'role' | 'skill' | 'goal') => void;

// TODO vraviraj: condense these props
export type PropsFromSkillSetFiltersHOC = {
  filterOptions?: FilterOptions;
  allOccupations?: AllOccupations;
  skillNames?: SkillNames;
  filterSelections?: FilterSelections;
  isFilterSelected: boolean;
  onRoleChange?: (key: string) => void;
  onSkillsChange?: (key: string) => void;
  onGoalChange?: (key: string) => void;
  onFilterReset?: () => void;
  onFilterInputChange?: FilterInputChangeHandler;
};

export type Props = PropsFromCaller &
  PropsFromSkillSetFiltersHOC &
  PropsFromSkillSetListQuery &
  PropsFromOccupationMetadataQuery;

type Props0 = Props & PropsFromGetContext;

export function filter(
  skillSets: TargetSkillProficiencySummary[],
  { role, goal, skills }: FilterSelections
): {
  filteredSkillSets: TargetSkillProficiencySummary[];
  filterOptions: FilterOptions;
  skillNames: SkillNames;
  isFilterSelected: boolean;
} {
  const filteredSkillSets: TargetSkillProficiencySummary[] = [];
  const skillNames: SkillNames = {};
  const filterOptions: FilterOptions = {
    roles: new Set(),
    skills: new Set(),
    goals: new Set(),
  };
  skillSets.forEach((skillSet) => {
    const { occupationId, goalType, targetSkillProficiencies } = skillSet;

    const matchesRole = role === '' || role === skillSet.occupationId;
    const matchesGoal = goal === '' || goal === skillSet.goalType;
    const matchesSkills = skills === '' || skillSet.targetSkillProficiencies.some(({ skillId }) => skills === skillId);

    if (occupationId && matchesGoal && matchesSkills) {
      filterOptions.roles.add(occupationId);
    }

    if (goalType && matchesRole && matchesSkills) {
      filterOptions.goals.add(goalType);
    }

    if (matchesRole && matchesGoal) {
      targetSkillProficiencies.forEach(({ skillId, skillName }) => {
        filterOptions.skills.add(skillId);
        skillNames[skillId] = skillName;
      });
    }

    if (matchesRole && matchesGoal && matchesSkills) {
      filteredSkillSets.push(skillSet);
    }
  });
  return { filteredSkillSets, filterOptions, skillNames, isFilterSelected: Boolean(role || goal || !skills) };
}

const SkillSetFiltersHOC = (Component: React.ComponentType<Props>) => {
  const SkillSetFilterHOC0 = ({ skillsets: skillSets, _eventData, _withTrackingData, ...props }: Props0) => {
    const location = useLocation();
    const navigate = useNavigate();

    const {
      role: routeRoleSelection = '',
      goal: routeGoalSelection = '',
      skills: routeSkillsSelections = '',
    } = location.query;

    const filterSelections: FilterSelections = useMemo(
      () => ({
        role: routeRoleSelection,
        goal: routeGoalSelection,
        skills: routeSkillsSelections,
      }),
      [routeGoalSelection, routeRoleSelection, routeSkillsSelections]
    );

    const filterSelectionsRef = useRef<FilterSelections>(filterSelections);

    const updateQueryParams = useCallback(
      (updates: Record<string, string | undefined>) => {
        const params = { ...location.query, ...updates };

        const filteredParams: Record<string, string> = {};

        Object.entries(params).forEach(([key, value]) => {
          if (typeof value === 'string' && value !== '') {
            filteredParams[key] = value;
          }
        });

        const searchParams = new URLSearchParams(filteredParams);
        const searchString = searchParams.toString();
        const formattedSearchString = searchString ? `?${searchString}` : '';

        navigate(
          {
            pathname: location.pathname,
            search: formattedSearchString,
          },
          { replace: true }
        );
      },
      [navigate, location.pathname, location.query]
    );

    type HandleChangeFunction = (debouncedFn: () => void) => void;
    const handleChange = useCallback<HandleChangeFunction>((debouncedFn) => {
      debouncedFn();
    }, []);

    const debouncedRoleChange = useDebounce(() => {
      const value = filterSelectionsRef.current.role;
      if (value) {
        Retracked.trackComponent(
          _eventData,
          { skillSetFilters: { ...filterSelectionsRef.current, role: value } },
          'skillset_filters_role_selection',
          'change',
          _withTrackingData
        );
      }
      updateQueryParams({ role: value });
    }, 300);

    const debouncedSkillsChange = useDebounce(() => {
      const value = filterSelectionsRef.current.skills;
      if (value) {
        Retracked.trackComponent(
          _eventData,
          { skillSetFilters: { ...filterSelectionsRef.current, skills: value } },
          'skillset_filters_skills_selection',
          'change',
          _withTrackingData
        );
      }
      updateQueryParams({ skills: value });
    }, 300);

    const debouncedGoalChange = useDebounce(() => {
      const value = filterSelectionsRef.current.goal;
      if (value) {
        Retracked.trackComponent(
          _eventData,
          { skillSetFilters: { ...filterSelectionsRef.current, goal: value } },
          'skillset_filters_goal_selection',
          'change',
          _withTrackingData
        );
      }
      updateQueryParams({ goal: value });
    }, 300);

    type SingleFilterChangeHandler = (value?: string) => void;
    const onRoleChange = useCallback<SingleFilterChangeHandler>(
      (value?: string) => {
        filterSelectionsRef.current = { ...filterSelectionsRef.current, role: value ?? '' };
        handleChange(debouncedRoleChange);
      },
      [handleChange, debouncedRoleChange]
    );

    const onSkillsChange = useCallback<SingleFilterChangeHandler>(
      (value?: string) => {
        filterSelectionsRef.current = { ...filterSelectionsRef.current, skills: value ?? '' };
        handleChange(debouncedSkillsChange);
      },
      [handleChange, debouncedSkillsChange]
    );

    const onGoalChange = useCallback<SingleFilterChangeHandler>(
      (value?: string) => {
        filterSelectionsRef.current = { ...filterSelectionsRef.current, goal: value ?? '' };
        handleChange(debouncedGoalChange);
      },
      [handleChange, debouncedGoalChange]
    );

    type FilterResetHandler = () => void;
    const onFilterReset = useCallback<FilterResetHandler>(() => {
      filterSelectionsRef.current = { role: '', skills: '', goal: '' };
      updateQueryParams({
        role: undefined,
        skills: undefined,
        goal: undefined,
      });
    }, [updateQueryParams]);

    const onFilterInputChange = useCallback<FilterInputChangeHandler>(
      (e, type) => {
        if (e !== '') {
          const propertyName = type + 'Input';
          Retracked.trackComponent(
            _eventData,
            { skillSetFilters: { ...filterSelectionsRef.current, [propertyName]: e } },
            'skillset_filters_input_typing',
            'change',
            _withTrackingData
          );
        }
      },
      [_eventData, _withTrackingData]
    );
    const { filteredSkillSets, filterOptions, skillNames, isFilterSelected } = filter(skillSets, filterSelections);
    return (
      <Component
        {...props}
        skillsets={filteredSkillSets}
        filterOptions={filterOptions}
        skillNames={skillNames}
        filterSelections={filterSelections}
        onRoleChange={onRoleChange}
        onSkillsChange={onSkillsChange}
        onGoalChange={onGoalChange}
        onFilterReset={onFilterReset}
        onFilterInputChange={onFilterInputChange}
        isFilterSelected={isFilterSelected}
      />
    );
  };
  return SkillSetFilterHOC0;
};

export const enhancer = compose<Props, PropsFromCaller>(
  setDisplayName('SkillSetFiltersHOC'),
  graphql<
    PropsFromCaller,
    GetAllOccupationsQueryData,
    GetAllOccupationsQueryVariables,
    PropsFromOccupationMetadataQuery
  >(GetAllOccupationsQuery, {
    options: { ssr: false, context: { clientName: 'gatewayGql' } },
    props: ({ data }): PropsFromOccupationMetadataQuery => {
      const occupations = filterExistsOrDefault(data?.Occupations?.getAllOccupations?.elements);
      const allOccupations = occupations.reduce((memo, { id, name }) => {
        // eslint-disable-next-line no-param-reassign
        memo[id] = name;
        return memo;
      }, {} as AllOccupations);
      return { allOccupations };
    },
  }),
  getContext({
    _eventData: PropTypes.object,
    _withTrackingData: PropTypes.func,
  }),
  SkillSetFiltersHOC,
  Retracked.createTrackedContainer<Props>(({ filterSelections }) => ({ skillSetFilters: filterSelections }))
);

export default enhancer;
